import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ReactSwitch from 'react-switch'
import {
  getAppointmentComment,
  getAppointmentDate,
  getAppointmentNotification,
  getAppointmentSalon,
  getAppointmentServicesFull,
  getAppointmentTime,
  setAppointmentNotification,
} from 'src/store/appointment.store'
import { declOfNum } from 'utils/declOfNum'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import { DateInfo } from 'components/Profile/VisitInfo/DateInfo'
import { SalonInfo } from 'components/Profile/VisitInfo/SalonInfo'
import { ClientSummary } from '../ClientsSummary/ClientsSummary'
import { NotificationDialog } from '../NotificationDialog/NotificationDialog'
import { useClientsList } from '../useClientsList'
import styles from './AppointmentConfirm.module.scss'

type AppointmentConfirmProps = {
  onSubmit: () => void,
}

/** «Проверьте данные записи»: summary, reminder switch and the final button */
export const AppointmentConfirm: React.FC<AppointmentConfirmProps> = ({ onSubmit }) => {
  const dispatch = useDispatch()
  const salon = useSelector(getAppointmentSalon)
  const clients = useClientsList(useSelector(getAppointmentServicesFull))
  const date = useSelector(getAppointmentDate)
  const time = useSelector(getAppointmentTime)
  const comment = useSelector(getAppointmentComment)
  const notification = useSelector(getAppointmentNotification)
  const [isNotificationDialogOpen, setNotificationDialogOpen] = useState(false)

  const submitDisabled = !salon || !clients.some((services) => services.length > 0) || !date || !time

  const handleNotificationSwitch = useCallback((enable: boolean) => {
    if (enable) {
      setNotificationDialogOpen(true)
    } else {
      dispatch(setAppointmentNotification(null))
    }
  }, [dispatch])

  const handleSubmitNotification = useCallback((hours: number) => {
    dispatch(setAppointmentNotification(hours))
    setNotificationDialogOpen(false)
  }, [dispatch])

  return (
    <div className={styles.container}>
      <div className={styles.clients}>
        {clients.map((services, index) => (
          <div key={index} className={styles.client}>
            <div className={styles.summary}>
              {clients.length > 1 &&
                <div className={styles.clientHeader}>
                  <span>Гость №{index + 1}</span>
                </div>}
              {services.length > 0 &&
                <ClientSummary index={index} totalClients={1} services={services} salon={salon} />}
            </div>
          </div>
        ))}
      </div>
      <div className={styles.note}>
        * Уважаемые гости, в меню услуг указано среднее время выполнения процедуры. <br />
        Время, необходимое для оказания услуг, индивидуальнодля каждого гостя и зависит от состояния ногтевой <br />
        пластины, кутикулы, кожи рук и ног.
      </div>
      <div className={styles.info}>
        {date && time && <DateInfo className={styles.dateInfo} date={date} time={time} />}
        <SalonInfo className={styles.salonInfo} withPhone salon={salon} />
      </div>
      <label className={styles.notification}>
        <ReactSwitch
          checked={notification !== null}
          onChange={handleNotificationSwitch}
          uncheckedIcon={false}
          checkedIcon={false}
          height={24}
          width={48}
        />
        <span>
          {notification === null
            ? 'Напомнить о записи'
            : `Напомнить о записи за ${notification} ${declOfNum(notification, ['час', 'часа', 'часов'])}`}
        </span>
      </label>
      {comment.length > 0 && (
        <div className={styles.comment}>
          <div className={styles.commentTitle}>
            Ваш комментарий:
          </div>
          <div>
            {comment}
          </div>
        </div>
      )}
      <div className={styles.actions}>
        <AppButton disabled={submitDisabled} onClick={onSubmit}>Подтвердить</AppButton>
      </div>
      <NotificationDialog
        isOpen={isNotificationDialogOpen}
        onClose={() => setNotificationDialogOpen(false)}
        onSubmit={handleSubmitNotification}
      />
    </div>
  )
}
